import {
    ChangeDetectorRef, ElementRef, EventEmitter,
    HostListener, Input, OnChanges, OnDestroy, Output, ViewChild
} from '@angular/core';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { orderBy } from 'lodash';
import { GridDetails, GridData, GridHeader, GridEvent, CheckBoxEvent } from '../../../sample-tracking/tracking-overview/interfaces/grid';
import { Pager } from '../pagination/pagination.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TrackingGridService } from '../tracking-grid.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MdlTooltipComponent } from '@angular-mdl/core';

@Component({
    selector: 'app-tracking-grid',
    template: require('./tracking-grid.component.html'),
    styles: [require('./tracking-grid.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrackingGridComponent implements OnChanges, OnDestroy {

    @Input()
    public grid: GridDetails;
    @Input()
    public isLoadingCompleted: boolean;
    @Input()
    public showPagination: boolean;
    @Input()
    public showSearchOrFilter: boolean;
    @Input()
    public setDefaultWidth = true;
    @Input()
    public paginationPagePerItems = [10, 50, 100, 200];
    @Input()
    public initialPageSize = 10;
    @Input()
    public loadingText = 'Loading Aliquots Details...';
    @Input()
    public emptyDataPlaceHolder = 'No Aliquots available.';
    @Input()
    public skipDefaultOrderBy = false;
    @Input()
    public hideDefaultSortIcon = true;
    @Input()
    public defaultOrderByField = 'accessionNumber';
    @Input()
    public draggableColumn = false;
    @Input()
    public defaultSortingOrder = 'asc';
    @Input()
    public loadColumnFilterOnChange = true;
    @Input()
    public handleDateTimeFormat = true;
    @Input()
    public showChildRows = false;
    @Input()
    public reasonList: { header: string, otherReason?: string, list: { key: string, value: string, showOther?: boolean }[] };
    @Input()
    public singleRowSelect: boolean;
    @Input()
    public selectType = 'checkbox';
    @Input()
    public cellToolTipText = '';

    @Output()
    public onRowSelected: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onItemClick: EventEmitter<GridEvent> = new EventEmitter<GridEvent>();

    @Output()
    public onColumnOrderChange: EventEmitter<GridHeader[]> = new EventEmitter<GridHeader[]>();

    @Output()
    public onDropDownSelect: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onReasonSelect: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onSubmitted: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onPagePerItemsEvent = new EventEmitter<Pager>();

    @ViewChild('tableheader', { read: ElementRef })
    public tableheader: ElementRef;

    @Output()
    public onMisMatchIconEvent: EventEmitter<any> = new EventEmitter<any>();

    public filterData: GridData[];

    public paginationData: GridData[];

    public gridCount: number;

    public currentPage = 1;
    public pager = { currentPage: this.currentPage, pageSize: this.initialPageSize } as Pager;
    public selectedElement: HTMLElement = null;
    public selectedelementId = '';
    public showShipmentDisclaimer = false;
    public showTooltip: { [key: string]: boolean } = {};
    private headerSearchValue: Subject<{}> = new Subject<{}>();
    private searchSubscription: Subscription;
    constructor(
        private changeDectRef: ChangeDetectorRef,
        private gridService: TrackingGridService
    ) {
        this.searchSubscription = this.headerSearchValue.pipe(
            debounceTime(200),
        ).subscribe((searchData: { headerKey: string, text: string }) => {
            const headerKey = searchData.headerKey as keyof GridData;
            const selectedHeader = this.grid.header.filter((gh: GridHeader) => gh.key === headerKey)[0];
            if (selectedHeader) {
                selectedHeader.headerSearchText = searchData.text;
            }
            this.paginationData = this.gridService.searchOnFilterCheckBoxChange(
                this.grid,
                this.gridService.onHeaderSearch(this.grid, this.grid.data));

            this.setPagination(this.paginationData);
        });
    }

    @HostListener('document:click', ['$event.target'])
    public onOutsideClick(targetElement: HTMLElement): void {
        if (targetElement &&
            (targetElement.className !== 'filter-menu is-active') &&
            (targetElement.className !== 'filter-img') &&
            (targetElement.offsetParent &&
                targetElement.offsetParent.className !== 'filter-menu is-active')) {
            const isFilterMenuActive = document.getElementsByClassName('filter-menu is-active').length;
            if (isFilterMenuActive) {
                this.onFilterClick('');
            }
        }
    }

    // Adding icons for the different status for shipmentStatus
    public getStatusIcon(courierHealthStatus: string | null | undefined): string | null {
        if (!courierHealthStatus) { return null; }

        // Find the color dynamically
        const getSelectedStatuscolor = (courierHealthStatus as string).toLowerCase();

        // Return the corresponding icon 
        return  `../../../../../images/${getSelectedStatuscolor}Icon.svg`;
        }

    public ngOnChanges(): void {
        if (this.pager.pageSize !== this.initialPageSize) {
            this.pager.pageSize = this.initialPageSize;
        }
        this.paginationData = [];

        if (this.grid && this.grid.data && this.grid.data.length) {
            if (!this.skipDefaultOrderBy) {
                this.grid.header.forEach((gh: GridHeader) => {
                    gh.sort = gh.key === this.defaultOrderByField ? this.defaultSortingOrder : null;
                });
            }

            this.paginationData = this.skipDefaultOrderBy ? this.grid.data : orderBy(this.grid.data, [this.defaultOrderByField], [this.defaultSortingOrder]);
            if (this.showSearchOrFilter) {
                setTimeout(() => { this.gridService.setUniqueValues(this.grid); }, 0);
            }
            
        }
        this.setPagination(this.paginationData);
    }

    /*
        works when sorting changes on header.
    */
    public sort(header: GridHeader) {
        this.grid.header.forEach((gh: GridHeader) => {
            if (gh.key === header.key && this.grid.data && this.grid.data.length) {
                gh.sort = (gh.sort === 'asc' ? 'desc' : gh.sort === 'desc' ? 'asc' : gh.sort === null ? 'asc' : null);
                this.paginationData = this.gridService.sortByHeader((!gh.sort || gh.sort === 'asc'), header, this.paginationData);
            } else {
                gh.sort = null;
            }
        });
        this.pager.currentPage = 1;
        this.setPagination(this.paginationData);
    }

    /*
        works on column data click
        emit event to parent component
    */
    public onGridItemClick(header: GridHeader, data: GridData, isChild = false) {
        this.onItemClick.emit({ data, header, isChild });
    }

    /*
        works when header search box value changes
    */
    public onHeaderSearchBoxChange(headerKey: string, searchText: string) {
        const searchData = {
            headerKey,
            text: searchText
        };
        this.pager.currentPage = 1;
        const selectedHeader = this.grid.header.filter((gh: GridHeader) => gh.key === headerKey)[0];
        if (selectedHeader) {
            selectedHeader.headerSearchText = searchData.text;
        }
        this.paginationData = this.gridService.searchOnFilterCheckBoxChange(
            this.grid,
            this.gridService.onHeaderSearch(this.grid, this.grid.data));
        this.setPagination(this.paginationData);
    }

    /* 
        works when typing on filter menu search box 
        used to filter unique items 
    */
    public onFilterSearchBoxChange(headerKey: string, searchText: string) {
        const selectedHeader = this.grid.header.filter(gh => gh.key === headerKey)[0];
        if (selectedHeader) {
            selectedHeader.filteredUniqueItems = selectedHeader.uniqueItems.
                filter(item => item.label.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
        }
    }

    /* 
        works on checkbox change inside filter menu.
        used to select/unselect checkboxes.
    */
    public onFilterCheckBoxChange(ce: CheckBoxEvent) {
        const selectedCheckBox = this.grid.header.filter((gh: GridHeader) => gh.key === ce.key)[0];
        if (selectedCheckBox) {
            if (ce.id === -1) {
                selectedCheckBox.filteredUniqueItems.forEach(fui => fui.isChecked = ce.isChecked);
                selectedCheckBox.selectedFilterCount = ce.isChecked ? null : '0 Item Selected';
            } else {
                const selectedUniqueItem = selectedCheckBox.filteredUniqueItems.filter(fui => fui.id === ce.id)[0];
                if (selectedUniqueItem) {
                    selectedUniqueItem.isChecked = ce.isChecked;
                }

                const selectAllCheckboxElement = document.getElementById(selectedCheckBox.key + '-1') as HTMLInputElement;
                const selectedStatus = selectedCheckBox.filteredUniqueItems.map(fui => fui.isChecked);
                const isAllCheckBoxSelected = selectedStatus.every(Boolean);
                const isSomeCheckBoxSelected = selectedStatus.some(Boolean);
                selectAllCheckboxElement.checked = isAllCheckBoxSelected;
                selectAllCheckboxElement.indeterminate =
                    !isAllCheckBoxSelected && isAllCheckBoxSelected !== isSomeCheckBoxSelected;

                selectedCheckBox.selectedFilterCount = !isAllCheckBoxSelected && selectedStatus.filter(ss => ss).length + ' Item Selected';
            }
        }

        this.paginationData = this.gridService.searchOnFilterCheckBoxChange(
            this.grid,
            this.gridService.onHeaderSearch(this.grid, this.grid.data));

        this.pager.currentPage = 1;
        this.setPagination(this.paginationData);
    }

    /*
        works when pagination changes
    */
    public onPageChange(pager: Pager) {
        this.pager = pager;
        this.setPagination(this.paginationData);
        this.onPagePerItemsEvent.emit(this.pager);
    }

    /* 
        works when filter image click on header.
        used to show/hide filter menu. 
    */
    public onFilterClick(headerKey: string) {
        this.grid.header.forEach((gh: GridHeader) => {
            if (gh.key === headerKey) {
                const element = document.getElementById(gh.key) as HTMLElement;
                if (element) {
                    setTimeout(function () { element.classList.toggle('is-active'); }, 100);
                }
            } else {
                const element = document.getElementById(gh.key) as HTMLElement;
                if (element) {
                    element.classList.remove('is-active');
                }
            }
        });
    }

    public onColumnHeaderDrop(event: CdkDragDrop<GridHeader[]>) {
        let header = [...this.grid.header];
        moveItemInArray(header, event.previousIndex, event.currentIndex);
        this.grid = {
            header: header, data: this.grid.data
        };
        this.onColumnOrderChange.emit(header);
        this.changeDectRef.markForCheck();
    }

    public onGroupingRowClick(index: number) {
        const groupingElement = document.getElementById('grouping-row-' + index);
        if (groupingElement) {
            groupingElement.classList.toggle('show');
        }

        const tableRowElement = document.getElementById('table-row-' + index);
        if (tableRowElement) {
            tableRowElement.classList.toggle('collapse');
        }
    }

    public ngOnDestroy() {
        this.searchSubscription.unsubscribe();
        this.showTooltip = {};
    }

    public onSelectAllRow(isChecked: boolean) {
        this.grid.header[0].allRowSelected = isChecked;
        this.grid.data.forEach((gd: any) => {
            gd.rowSelected = isChecked;
        });
        this.onRowSelected.emit(this.grid.data);
        this.changeDectRef.markForCheck();
    }

    public onRowSelect(id: number, header: GridHeader, isChecked: boolean) {
        this.grid.header[0].allRowSelected = false;
        this.grid.data.forEach((t: any) => {
            if (t[header.selectId as keyof GridData] === id) {
                t.rowSelected = isChecked;
            }
            if (t[header.selectId as keyof GridData] !== id && this.singleRowSelect) {
                t.rowSelected = false;
            }
        });
        this.onRowSelected.emit(this.grid.data);
        this.changeDectRef.markForCheck();
    }

    public handleNgClass(header: GridHeader, rowData: string) {
        if (!header.cssClasses || !rowData || !rowData.length) { return ''; }
        let className = '';
        header.cssClasses.forEach(t => {
            if (t.key.toLowerCase() === rowData.toLowerCase()) {
                className = t.class;
            }
        });
        return className;
    }

    public onDropdownChange(selectedValue: any, header: GridHeader, data: any) {
        if (!selectedValue || !header.dropDownList) { return; }
        let value = header.dropDownList.find(t => t.key === selectedValue || t.value === selectedValue);
        this.onDropDownSelect.emit({ header: header, data: data, selectedValue: value });
    }

    public onReasonChange(selectedValue: any, data: any) {
        if (!selectedValue) { return; }
        let value = this.reasonList.list.find(t => t.key === selectedValue || t.value === selectedValue);
        data.showOtherReason = value.showOther;
        this.onReasonSelect.emit({ data: data, selectedValue: value });
    }

    public onSubmitClicked(data: any) {
        this.onSubmitted.emit(data);
    }

    public toggleTooltip(id: string, show: boolean) {
        this.showTooltip[id] = show;
    }

    public dataChangeClicked(data: any, header: GridHeader) {
        this.onMisMatchIconEvent.emit({ data, header });
    }

    /*
        set final grid data (filterData)
    */
    private setPagination(paginationData: GridData[]) {

        this.filterData = paginationData;
        if (this.showPagination) {

            this.gridCount = paginationData.length;
            this.currentPage = this.pager.currentPage;

            this.filterData = paginationData.slice(
                (this.pager.currentPage - 1) * this.pager.pageSize,
                this.pager.currentPage * this.pager.pageSize
            );

        }
        this.changeDectRef.markForCheck();
    }
}